import React, { type ReactNode } from 'react';

import type { IconProps } from '#@/components/Icon';
import { castArray } from '#@/shared/utils/javascript';

import { IconWrapper, InfoItem, Message, StyledIcon, Wrapper } from './Styles';
import type { CalloutSize, CalloutType, EnhancedCalloutType } from './types';
import { calloutTypeToIconTypeMap } from './utils';

export interface CalloutProps {
  className?: string;
  /**
   * `Callout` accepts either normal children or an array of elements. If an
   * array is passed in, multiple individual messages will be displayed in the
   * same wrapper.
   *
   * @example
   * ```
   * <Callout>{["foo", "bar"]}</Callout>
   * ```
   */
  children: ReactNode | ReactNode[];
  /**
   * `variant` can be "danger", "warning", "info", or "success".
   * For "regular" Callout it defaults to "info". For "compact" callout
   * we add a new variant "compactDefault" which is used when no variant
   * is specified.
   */
  variant?: CalloutType;
  /**
   * `size` can be either "compact" or "regular". Defaults to "regular".
   */
  size?: CalloutSize;
  /**
   * The icon to show next to the message. If multiple messages are provided,
   * each will have the icon. If the prop is ommitted, a default icon will be
   * displayed based on the variant, or none if the variant is `compact`. `null`
   * can be also passed to force the component to not render the icon.
   */
  icon?: IconProps['type'] | null;
  /**
   * Controls vertical placement of the icon (if one exists).
   */
  alignIcon?: 'center' | 'top';
  /**
   * A `suffix` is an element that will be rendered next to each message.
   */
  suffix?: React.ReactNode;
  /**
   * Set to true to add standard bottom margin (20px) to the `Callout`,
   * or specify a number to set a custom margin in pixels. Defaults to `false`.
   */
  marginBottom?: boolean | number;
  /*
   * Set to `false` to disallow the message to overflow the container when the Callout
   * is placed in a container with a fixed width. Defaults to `true`.
   */
  allowMessageOverflow?: boolean;
}

const mapPropsToInternalConfig = (props: CalloutProps) => {
  const type: EnhancedCalloutType =
    props.size === 'compact' ? (props.variant ?? 'compactDefault') : (props.variant ?? 'info');
  const icon =
    props.icon === undefined && props.size !== 'compact'
      ? calloutTypeToIconTypeMap[type]
      : (props.icon ?? null);

  return {
    children: props.children,
    type,
    size: props.size || 'regular',
    icon,
    suffix: props.suffix || null,
    marginBottom: props.marginBottom || false,
    alignIcon: props.alignIcon || 'center',
    allowMessageOverflow: props.allowMessageOverflow ?? true,
  };
};

export const Callout: React.FC<CalloutProps> = (props) => {
  const { children, type, size, icon, alignIcon, suffix, marginBottom, allowMessageOverflow } =
    mapPropsToInternalConfig(props);

  return (
    <Wrapper
      data-cy-info-message-type={type}
      $messageType={type}
      $size={size}
      $marginBottom={marginBottom}
      {...props}
    >
      {castArray(children).map((child, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <InfoItem key={i} $alignIcon={alignIcon}>
          {icon !== null && (
            <IconWrapper $size={size}>
              <StyledIcon
                $messageType={type}
                $size={size}
                type={icon}
                size={
                  {
                    compact: 12,
                    regular: 18,
                  }[size]
                }
              />
            </IconWrapper>
          )}
          <Message $allowOverflow={allowMessageOverflow}>{child}</Message>
          {suffix}
        </InfoItem>
      ))}
    </Wrapper>
  );
};
